import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useV1EventForm } from '../../../../hooks/useV1EventForm';
import { useEventContext } from '../../../contexts/EventContext';
import { useAccount } from '../../../../hooks/useAccount';
import { V1EventFormWelcome } from './V1EventFormWelcome';
import { V1EventFormQuestions } from './V1EventFormQuestions';
import { FormBuiltinFields } from '../../../../public_home/components/forms/FormBuiltinFields';
import { V1FormConfirmation } from './V1FormConfirmation';
import { V1FormRegistrationOptions } from '../../../../event_dashboard/v1/components/forms/V1FormRegistrationOptions';
import { FormContext } from '../../../../public_home/components/forms/FormContext';
import { AuthenticationRequired } from '../../../../components/common/AuthenticationRequired';
import { useSubmitV1EventForm } from '../../../../hooks/useV1EventFormSubmission';
import type { FormState, FormResponses } from '../../../../types/publicForm.types';
import { StructuredApiError } from '../../../../api/apiErrors';

/**
 * V1 Event Form Public Component - FROZEN
 *
 * Handles the complete V1 form flow:
 * 1. Welcome page (optional)
 * 2. Single page with all questions
 * 3. Confirmation page
 *
 * V1 forms do NOT support:
 * - Multi-page navigation
 * - Progress bars
 * - Complex conditional logic
 */
export const V1EventFormPublic: React.FC = () => {
  const { form_slug } = useParams<{ form_slug: string }>();
  const { eventSlug, event } = useEventContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: account, refetch: refetchAccount } = useAccount();

  // Fetch V1 form data with questions
  const { data: form, isLoading: isFormLoading, error: formError } = useV1EventForm(eventSlug, form_slug!);

  // Form state
  const [formState, setFormState] = useState<FormState & { generalError?: string }>({
    currentPageIndex: 0, // Always 0 for V1 forms
    formData: {},
    touchedFields: new Set(),
    visibleChildQuestions: new Set(),
    validationErrors: {},
    isSubmitting: false,
    showConfirmation: false,
    generalError: undefined,
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  // Check if returning from authentication
  const urlParams = new URLSearchParams(location.search);
  const isReturningFromAuth = urlParams.get('authenticated') === 'true';

  const [showWelcomePage, setShowWelcomePage] = useState(!isReturningFromAuth);
  const [builtinFieldsCompleted, setBuiltinFieldsCompleted] = useState(false);
  const [isRegistrationChecked, setIsRegistrationChecked] = useState(false);

  // Submit mutation
  const submitMutation = useSubmitV1EventForm();

  // Get primary color from event or default
  const primaryColor = event?.primaryColor || '#3B82F6';

  // Check authentication requirements
  const requiresAuth = form?.requiresAccount || false;
  const isAuthenticated = !!account;

  // Initialize registration checkbox based on form settings
  useEffect(() => {
    if (form?.allowRegistration && form?.registrationPreSelected) {
      setIsRegistrationChecked(true);
    }
  }, [form]);

  // Handle returning from authentication
  useEffect(() => {
    if (isReturningFromAuth) {
      refetchAccount().then(() => {
        // Remove the authenticated param from URL after handling
        const newParams = new URLSearchParams(location.search);
        newParams.delete('authenticated');
        const newSearch = newParams.toString();
        const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, '', newUrl);
      });
    }
  }, [isReturningFromAuth, refetchAccount, location]);

  // Field change handler
  const handleFieldChange = useCallback((questionId: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [questionId]: value,
      },
      // Clear validation error when field is changed
      validationErrors: {
        ...prev.validationErrors,
        [questionId]: undefined,
      },
      // Clear general error when user starts fixing issues
      generalError: undefined,
    }));
  }, []);

  // Field blur handler
  const handleFieldBlur = useCallback((questionId: string) => {
    setFormState(prev => ({
      ...prev,
      touchedFields: new Set([...prev.touchedFields, questionId]),
    }));
  }, []);

  // Validate all fields
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // V1 forms have questions directly on the form object
    const questions = form?.questions || [];

    // Helper to collect all child questions from answers
    const collectAllChildQuestions = (parentQuestions: Question[]): Question[] => {
      const childQuestions: Question[] = [];
      parentQuestions.forEach(q => {
        if (q.answers) {
          q.answers.forEach(answer => {
            if (answer.childQuestions) {
              childQuestions.push(...answer.childQuestions.filter(child => !child._destroy));
            }
          });
        }
      });
      return childQuestions;
    };

    // Get all possible child questions
    const allChildQuestions = collectAllChildQuestions(questions);
    const allQuestions = [...questions, ...allChildQuestions];

    // Create a Set of child question IDs for faster lookup
    const childQuestionIds = new Set(allChildQuestions.map(q => String(q.id)));

    // Validate each question
    allQuestions.forEach(question => {
      // Skip validation for conditional questions that aren't visible
      if (childQuestionIds.has(String(question.id)) && !formState.visibleChildQuestions.has(String(question.id))) {
        return;
      }

      const value = formState.formData[question.id] || '';

      // Required field validation
      if (question.required && !value) {
        errors[question.id] = 'This field is required';
      }

      // Character minimum validation - ONLY for text-based questions
      // Map answer styles to check if it's a text-based question
      const textBasedStyles = ['text', 'textarea', 'email', 'number', 'short_text', 'long_text', 'paragraph'];
      const isTextQuestion = textBasedStyles.includes(question.answerStyle || question.type || '');

      if (isTextQuestion) {
        const hasCharacterMinimum = (question.options?.useCharacterMinimum === "1" ||
                                    question.options?.useCharacterMinimum === true) &&
                                    (question.options?.characterMinimum || 0) > 0;

        if (hasCharacterMinimum && value) {
          const characterMinimum = question.options?.characterMinimum || 0;
          if (String(value).length < characterMinimum) {
            errors[question.id] = `Minimum ${characterMinimum} characters required (${String(value).length} entered)`;
          }
        }
      }
    });

    setFormState(prev => ({
      ...prev,
      validationErrors: errors,
      touchedFields: new Set(allQuestions.map(q => q.id)), // Touch all fields on submit
    }));

    return Object.keys(errors).length === 0;
  };

  // Submit handler
  const handleSubmit = async () => {
    // Check terms acceptance if terms exist
    if (form?.terms && !termsAccepted) {
      setShowTermsError(true);
      // Scroll to terms section
      const termsElement = document.getElementById('form-terms-section');
      termsElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(formState.validationErrors)[0];
      const element = document.getElementById(`question-${firstError}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setFormState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const result = await submitMutation.mutateAsync({
        eventSlug,
        formSlug: form_slug!,
        data: {
          ...formState.formData,
          responses: formState.formData,
          // Include registration option if enabled
          shouldRegister: form.allowRegistration ? isRegistrationChecked : false,
        },
      });

      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        showConfirmation: true,
        confirmationData: result,
      }));
    } catch (error: any) {
      console.error('Form submission error:', error);

      // Handle API validation errors
      // Check if it's a StructuredApiError with originalData
      let errorData = null;
      if (error instanceof StructuredApiError && error.originalData) {
        errorData = error.originalData;
        console.log('Processing StructuredApiError with validation_errors:', errorData.validation_errors);
      } else if (error.response?.data) {
        errorData = error.response.data;
        console.log('Processing raw error response with validation_errors:', errorData.validation_errors);
      }

      if (errorData) {

        // Check if we have detailed validation errors with question IDs
        if (errorData.validation_errors && Object.keys(errorData.validation_errors).length > 0) {
          const newValidationErrors: Record<string, string> = {};

          // Map API validation errors to our validation error state
          Object.entries(errorData.validation_errors).forEach(([questionId, errorInfo]: [string, any]) => {
            newValidationErrors[questionId] = errorInfo.message;
            console.log(`Setting validation error for question ${questionId}:`, errorInfo.message);
          });

          setFormState(prev => {
            const newState = {
              ...prev,
              isSubmitting: false,
              validationErrors: newValidationErrors,
              touchedFields: new Set([...prev.touchedFields, ...Object.keys(newValidationErrors)]),
              generalError: undefined, // Clear any general error
            };
            console.log('Setting form state with validation errors:', newState.validationErrors);
            return newState;
          });

          // Scroll to first error
          const firstErrorId = Object.keys(newValidationErrors)[0];
          if (firstErrorId) {
            setTimeout(() => {
              const element = document.getElementById(`question-${firstErrorId}`);
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
          }
          return; // Important: return here to prevent fallthrough
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Fallback to showing general errors if no detailed validation errors
          // Try to parse question names from error messages
          const newValidationErrors: Record<string, string> = {};

          errorData.errors.forEach((errorMessage: string) => {
            // Try to find a matching question by name
            const questions = form?.questions || [];
            const matchedQuestion = questions.find(q =>
              errorMessage.includes(q.name)
            );

            if (matchedQuestion) {
              newValidationErrors[matchedQuestion.id] = errorMessage;
            }
          });

          if (Object.keys(newValidationErrors).length > 0) {
            setFormState(prev => ({
              ...prev,
              isSubmitting: false,
              validationErrors: newValidationErrors,
              touchedFields: new Set([...prev.touchedFields, ...Object.keys(newValidationErrors)]),
            }));

            // Scroll to first error
            const firstErrorId = Object.keys(newValidationErrors)[0];
            setTimeout(() => {
              const element = document.getElementById(`question-${firstErrorId}`);
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
          } else {
            // If we can't map errors to questions, show a general error message
            setFormState(prev => ({
              ...prev,
              isSubmitting: false,
              generalError: errorData.errors.join(', '),
            }));
          }
        }
      } else {
        // Generic error
        setFormState(prev => ({
          ...prev,
          isSubmitting: false,
          generalError: 'An error occurred while submitting the form. Please try again.',
        }));
      }
    }
  };

  // Loading state
  if (isFormLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  // Error state
  if (formError || !form) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Form Not Available</h2>
          <p className="text-gray-600">This form is not currently available.</p>
          <button
            onClick={() => navigate(`/forms`)}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  // Confirmation page
  if (formState.showConfirmation) {
    return (
      <V1FormConfirmation
        form={form}
        confirmationData={formState.confirmationData!}
        primaryColor={primaryColor}
        onNewSubmission={() => {
          // Reset form state to start over
          setFormState({
            currentPageIndex: 0,
            formData: {},
            touchedFields: new Set(),
            visibleChildQuestions: new Set(),
            validationErrors: {},
            isSubmitting: false,
            showConfirmation: false,
            confirmationData: undefined,
          });
          setShowWelcomePage(true);
          setBuiltinFieldsCompleted(false);
        }}
        onEditSubmission={() => {
          if (formState.confirmationData?.editUrl) {
            // V1 forms navigate to edit URL directly
            window.location.href = formState.confirmationData.editUrl;
          }
        }}
      />
    );
  }

  // Welcome page
  if (showWelcomePage) {
    return (
      <V1EventFormWelcome
        form={form}
        primaryColor={primaryColor}
        eventSlug={eventSlug}
        onStart={() => {
          if (requiresAuth && !isAuthenticated) {
            setShowWelcomePage(false);
            return;
          }
          setShowWelcomePage(false);
        }}
      />
    );
  }

  // Authentication required
  if (requiresAuth && !isAuthenticated) {
    return (
      <div className="flex items-center justify-center p-4 min-h-[400px]">
        <div className="max-w-2xl w-full">
          <AuthenticationRequired
            context="form"
            entityName={form.name}
            primaryColor={primaryColor}
            onAuthenticated={() => {
              // The AuthenticationRequired component will submit a form that causes a page reload
              // We'll handle the actual state update when we return with ?authenticated=true
              // This callback may not actually be called due to the page reload
            }}
          />
        </div>
      </div>
    );
  }

  // Builtin fields (name, email, etc.)
  if (!builtinFieldsCompleted && !form.anonymous) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <FormBuiltinFields
            form={form}
            formData={formState.formData}
            onUpdateField={handleFieldChange}
            onContinue={() => {
              setBuiltinFieldsCompleted(true);
            }}
            onBack={() => setShowWelcomePage(true)}
            primaryColor={primaryColor}
          />
        </div>
      </div>
    );
  }

  // Main form with all questions - V1 forms have questions directly
  const questions = form.questions || [];

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => {
              if (builtinFieldsCompleted) {
                setBuiltinFieldsCompleted(false);
              } else {
                setShowWelcomePage(true);
              }
            }}
            className="flex items-center text-gray-600 hover:text-gray-900 bg-white px-3 py-1.5 rounded-lg shadow-sm mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>

          {/* Form header with background card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{form.name}</h1>
            {form.description && (
              <div
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: form.description }}
              />
            )}
          </div>
        </div>

        {/* Form content */}
        <div className="bg-white rounded-lg shadow-md">
          {/* General error message */}
          {formState.generalError && (
            <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{formState.generalError}</p>
            </div>
          )}

          <div className="p-6">
            <FormContext.Provider
              value={{
                state: formState,
                actions: {
                  updateField: handleFieldChange,
                  touchField: handleFieldBlur,
                  updateVisibleChildQuestions: (questionIds: Set<string>) => {
                    setFormState(prev => ({
                      ...prev,
                      visibleChildQuestions: questionIds
                    }));
                  },
                  validateField: (questionId: string) => {
                    // Validation is handled in handleFieldChange
                  },
                  setFormData: (data: FormResponses) => {
                    setFormState(prev => ({
                      ...prev,
                      formData: data
                    }));
                  },
                },
              }}
            >
              <V1EventFormQuestions
                questions={questions}
                formData={formState.formData}
                validationErrors={formState.validationErrors}
                touchedFields={formState.touchedFields}
                visibleChildQuestions={formState.visibleChildQuestions}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                primaryColor={primaryColor}
              />
            </FormContext.Provider>
          </div>

          {/* Registration Options */}
          {form.allowRegistration && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="mt-6">
                <V1FormRegistrationOptions
                  allowRegistration={form.allowRegistration}
                  registrationPreSelected={form.registrationPreSelected || false}
                  registerOnlyWhenApproved={form.registerOnlyWhenApproved || false}
                  registrationInstructions={form.registrationInstructions}
                  isRegistrationChecked={isRegistrationChecked}
                  onRegistrationChange={setIsRegistrationChecked}
                  primaryColor={primaryColor}
                  customRegistrationActions={form.event?.customRegistrationActions}
                />
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          {form.terms && (
            <div id="form-terms-section" className="px-6 pb-6 border-t border-gray-200">
              <div className="mt-6">
                <h3 className="text-base font-medium text-gray-900 mb-3">Terms</h3>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div
                    className="prose prose-sm max-w-none text-gray-700 mb-4"
                    dangerouslySetInnerHTML={{ __html: form.terms }}
                  />
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => {
                        setTermsAccepted(e.target.checked);
                        if (e.target.checked) {
                          setShowTermsError(false);
                        }
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      style={{ accentColor: primaryColor }}
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      I accept the terms and conditions
                    </span>
                  </label>
                  {showTermsError && !termsAccepted && (
                    <p className="mt-2 text-sm text-red-600">
                      You must accept the terms and conditions to submit this form
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit button */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={formState.isSubmitting || (form?.terms && !termsAccepted)}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                style={{ backgroundColor: formState.isSubmitting || (form?.terms && !termsAccepted) ? undefined : primaryColor }}
              >
                {formState.isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  form.options?.finishCallToAction || 'Submit'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};